import { K_BLACK, rgb } from './color.js';
import { getLocale } from './i18n.js';
import { SHEET_SIZES } from './units.js';
import { buildTemplate, relayoutDesign, scaleDesign, templateDefaultArea, templateInfo } from './templates.js';
import { eventSetsForNewYear, type EventSet } from './events.js';
import { computeImposition, computeImpositionForPiece, type Imposition } from './imposition.js';
import { TENT_PANEL_MONTHS, tentFlat, tentPagePanels, tentPanelSize, tentPieceSize, type TentFlat, type TentPanelKey } from './tent.js';
import {
  MONTH_KEYS,
  SCHEMA_VERSION,
  monthOfKey,
  type Element,
  type LayoutGrid,
  type PageDesign,
  type PageKey,
  type Product,
  type ProjectDoc,
  type Rect,
} from './schema.js';

export const DEFAULT_PRODUCT: Product = {
  productType: 'monthly',
  sheetPreset: 'A4',
  orientation: 'portrait',
  widthMm: 210,
  heightMm: 297,
  includeCover: false,
  backingBoard: { enabled: false, printed: false, note: '' },
  bleedMm: 3,
  safeMarginMm: 5,
  bindingEdge: 'top',
  bindingClearanceMm: 12,
  cropMarks: true,
  finishing: 'Wire-O binding with hanger at the top (configurable — confirm clearance with the binding equipment)',
  calendarArea: 'bottom',
  duplex: false,
  tent: { baseMm: 42, tabMm: 15, layout: 'classic' },
  carrier: { enabled: false, widthMm: 99, heightMm: 210, pad: { x: 7, y: 148, w: 85, h: 55 } },
};

export const DEFAULT_BRAND: ProjectDoc['brand'] = {
  presetId: null, customerName: '', logoAssetId: null, contact: '', colors: [K_BLACK, rgb('#1f3a5f')],
  company: '', tagline: '', phone: '', email: '', website: '', address: '', personName: '', personTitle: '',
};

/** Product defaults for a product type (finishing differs: wall calendars hang from a binding). */
export function productDefaults(type: Product['productType']): Partial<Product> {
  if (type === 'year') return { productType: 'year', bindingEdge: 'none', bindingClearanceMm: 0, includeCover: false, duplex: false, calendarArea: 'full', finishing: 'Single sheet, trimmed (configurable).' };
  if (type === 'tent') return { productType: 'tent', bindingEdge: 'none', bindingClearanceMm: 0, includeCover: false, duplex: false, calendarArea: 'full', cropMarks: true, finishing: 'Folded tent card: score and fold, glue tab under the base (configurable).' };
  return { productType: 'monthly', bindingEdge: 'top', bindingClearanceMm: 12, calendarArea: 'bottom', finishing: DEFAULT_PRODUCT.finishing };
}

export const DEFAULT_LAYOUT_GRID: LayoutGrid = { cols: 6, rows: 8, gutterMm: 4, snap: false, show: false };

export function newProjectDoc(opts: { year?: number; templateId?: string; product?: Partial<Product>; eventSets?: EventSet[]; layoutGrid?: Partial<LayoutGrid> } = {}): ProjectDoc {
  const templateId = opts.templateId ?? 'photo-top';
  const type = templateInfo(templateId).product;
  const merged: Product = { ...DEFAULT_PRODUCT, ...productDefaults(type), calendarArea: templateDefaultArea(templateId), ...opts.product, productType: type };
  const size = sheetSize(merged);
  const product = { ...merged, widthMm: size.w, heightMm: size.h };
  const designs = buildTemplate(templateId, { w: product.widthMm, h: product.heightMm }, product);
  return {
    schemaVersion: SCHEMA_VERSION,
    year: opts.year ?? 2027,
    templateId,
    product,
    calendar: { weekStart: 'monday', rows: 'actual', adjacent: 'blank', weekNumbers: false, locale: 'en' },
    designs,
    overrides: {},
    photos: {},
    photoPool: [],
    captions: {},
    brand: { ...DEFAULT_BRAND },
    eventSets: opts.eventSets ?? [],
    cellOverrides: {},
    fontLock: {},
    layoutGrid: { ...DEFAULT_LAYOUT_GRID, ...opts.layoutGrid },
    checks: { ppiTarget: 300, ppiMinimum: 150, acknowledged: [] },
  };
}

/** Sheet size from preset + orientation (custom keeps its own dimensions). */
export function sheetSize(p: Pick<Product, 'sheetPreset' | 'orientation' | 'widthMm' | 'heightMm'>) {
  if (p.sheetPreset === 'custom') return { w: p.widthMm, h: p.heightMm };
  const s = SHEET_SIZES[p.sheetPreset];
  return p.orientation === 'portrait' ? { w: s.w, h: s.h } : { w: s.h, h: s.w };
}

/** Apply a product change, rescaling designs when the sheet size changes. */
export function applyProductChange(doc: ProjectDoc, patch: Partial<Product>): ProjectDoc {
  const before = { w: doc.product.widthMm, h: doc.product.heightMm };
  const product = { ...doc.product, ...patch };
  const size = sheetSize(product);
  product.widthMm = size.w;
  product.heightMm = size.h;
  let designs = doc.designs;
  const keys = ['month', 'cover', 'backing', 'year', 'face', 'info'] as const;
  const aspectChanged = Math.sign(size.w - size.h) !== Math.sign(before.w - before.h) || Math.abs(size.w / size.h - before.w / before.h) > 0.15;
  const marginsChanged = (['bleedMm', 'safeMarginMm', 'bindingEdge', 'bindingClearanceMm'] as const).some((k) => k in patch && patch[k] !== doc.product[k]);
  if (size.w !== before.w || size.h !== before.h) {
    const scaled = Object.fromEntries(keys.map((k) => [k, scaleDesign(doc.designs[k], before, size)])) as ProjectDoc['designs'];
    if (product.orientation !== doc.product.orientation || aspectChanged) {
      // orientation/proportion change (e.g. A4 → DL): rebuild geometry from the template, keep styles
      const fresh = buildTemplate(doc.templateId, size, product);
      designs = Object.fromEntries(keys.map((k) => [k, relayoutDesign(scaled[k], fresh[k])])) as ProjectDoc['designs'];
    } else designs = scaled;
  } else if (marginsChanged && product.productType === 'year') {
    const fresh = buildTemplate(doc.templateId, size, product);
    designs = { ...designs, year: relayoutDesign(designs.year, fresh.year) };
  }
  // tent panels or carrier size changed: rebuild those geometries, keep styles
  if (patch.tent && JSON.stringify(patch.tent) !== JSON.stringify(doc.product.tent)) {
    const fresh = buildTemplate(doc.templateId, size, product);
    designs = { ...designs, face: relayoutDesign(designs.face, fresh.face), info: relayoutDesign(designs.info, fresh.info) };
  }
  if (patch.carrier && (patch.carrier.widthMm !== doc.product.carrier.widthMm || patch.carrier.heightMm !== doc.product.carrier.heightMm || JSON.stringify(patch.carrier.pad) !== JSON.stringify(doc.product.carrier.pad))) {
    const fresh = buildTemplate(doc.templateId, size, product);
    designs = { ...designs, carrier: relayoutDesign(designs.carrier, fresh.carrier) };
  }
  if (patch.calendarArea && patch.calendarArea !== 'custom' && patch.calendarArea !== doc.product.calendarArea) {
    const fresh = buildTemplate(doc.templateId, size, product);
    designs = { ...designs, month: relayoutDesign(designs.month, fresh.month) };
  }
  return { ...doc, product, designs };
}

/** Switch template: new geometry and styles; content (photos, captions, events) is kept. Overrides are cleared. */
export function applyTemplate(doc: ProjectDoc, templateId: string): ProjectDoc {
  const size = sheetSize(doc.product);
  // Each template has its own calendar area (e.g. the full-page grid has no photo);
  // the designer can still change it afterwards in Product → Calendar area.
  const type = templateInfo(templateId).product;
  const switching = type !== doc.product.productType;
  const product = { ...doc.product, ...(switching ? productDefaults(type) : {}), productType: type, calendarArea: templateDefaultArea(templateId) };
  return { ...doc, templateId, product, designs: buildTemplate(templateId, size, product), overrides: {} };
}

export type DesignKey = 'month' | 'cover' | 'backing' | 'year' | 'face' | 'carrier' | 'info';
export interface PageInfo {
  key: PageKey;
  label: string;
  kind: 'cover' | 'month' | 'backing' | 'year' | 'face' | 'back' | 'carrier' | 'info';
  month: number | null;
  design: DesignKey;
}

/** Ordered printable pages of the calendar (cover first when enabled). */
export function calendarPages(doc: ProjectDoc): PageInfo[] {
  const loc = getLocale(doc.calendar.locale);
  const pages: PageInfo[] = [];
  const type = doc.product.productType;
  if (type === 'year') return [{ key: 'year', label: `Year ${doc.year}`, kind: 'year', month: null, design: 'year' }];
  if (type === 'tent') return tentPagePanels(doc.product).map((p) => tentPageInfo(doc, p.key));
  if (doc.product.includeCover) pages.push({ key: 'cover', label: 'Cover', kind: 'cover', month: null, design: 'cover' });
  MONTH_KEYS.forEach((k, i) => pages.push({ key: k, label: loc.months[i], kind: 'month', month: i + 1, design: 'month' }));
  // double-sided leaves with a cover end on a back cover (e.g. 7 leaves = 14 sides)
  if (doc.product.duplex && doc.product.includeCover) pages.push({ key: 'back', label: 'Back cover', kind: 'back', month: null, design: 'backing' });
  return pages;
}
/**
 * Pages in press order. Double-sided desk-tent leaves are read down the front
 * (cover, Jan–Jun), then the stand is turned round and the backs are read
 * (Jul–Dec, back cover). So leaf i carries reading page i on its front and
 * page n−1−i on its back: cover|back cover, Jan|Dec, Feb|Nov … Jun|Jul.
 * Output order is front, back, front, back … for duplex printing.
 */
export function printOrder(doc: ProjectDoc): PageInfo[] {
  const pages = calendarPages(doc);
  if (doc.product.productType !== 'monthly' || !doc.product.duplex) return pages;
  const n = pages.length, out: PageInfo[] = [];
  for (let i = 0; i < Math.ceil(n / 2); i++) {
    out.push(pages[i]);
    if (n - 1 - i > i) out.push(pages[n - 1 - i]);
  }
  return out;
}
/** Separate artwork printed on other stock: tear-off carrier card, backing board or reverse side. */
export function backingPage(doc: ProjectDoc): PageInfo | null {
  if (doc.product.productType === 'monthly' && doc.product.carrier.enabled) return { key: 'carrier', label: 'Carrier card', kind: 'carrier', month: null, design: 'carrier' };
  return doc.product.backingBoard.enabled && doc.product.backingBoard.printed && doc.product.productType !== 'tent'
    ? { key: 'backing', label: doc.product.productType === 'year' ? 'Reverse side' : 'Backing board', kind: 'backing', month: null, design: 'backing' }
    : null;
}
export function allPages(doc: ProjectDoc): PageInfo[] {
  const b = backingPage(doc);
  return b ? [...calendarPages(doc), b] : calendarPages(doc);
}
export function pageInfo(doc: ProjectDoc, key: PageKey): PageInfo {
  const p = allPages(doc).find((x) => x.key === key);
  if (p) return p;
  const month = monthOfKey(key);
  if (month) return { key, label: getLocale('en').months[month - 1], kind: 'month', month, design: 'month' };
  if (key === 'cover') return { key, label: 'Cover', kind: 'cover', month: null, design: 'cover' };
  if (key === 'year') return { key, label: `Year ${doc.year}`, kind: 'year', month: null, design: 'year' };
  if (key in TENT_PANEL_MONTHS || key === 'info') return tentPageInfo(doc, key as TentPanelKey);
  if (key === 'back') return { key, label: 'Back cover', kind: 'back', month: null, design: 'backing' };
  if (key === 'carrier') return { key, label: 'Carrier card', kind: 'carrier', month: null, design: 'carrier' };
  return { key: 'backing', label: 'Backing board', kind: 'backing', month: null, design: 'backing' };
}

function tentPageInfo(doc: ProjectDoc, key: TentPanelKey): PageInfo {
  const layout = doc.product.tent.layout;
  const portrait = layout === 'dl-portrait-6' || layout === 'dl-portrait-4';
  const label: Partial<Record<TentPanelKey, string>> = portrait
    ? { faceA: 'Panel 1 (Jan–Jun)', faceB: 'Panel 2 (Jul–Dec)', info: 'Panel 3 (side information)', tri1: 'Panel 1 (Jan–Apr)', tri2: 'Panel 2 (May–Aug)', tri3: 'Panel 3 (Sep–Dec)' }
    : { faceA: 'Front face (Jan–Jun)', faceB: 'Back face (Jul–Dec)', info: 'Base (other information)' };
  return { key, label: label[key] ?? key, kind: key === 'info' ? 'info' : 'face', month: null, design: key === 'info' ? 'info' : 'face' };
}

/** Months shown by a year-grid on a page: tent panels show half a year or four months. */
export function pageMonths(key: PageKey): number[] {
  return TENT_PANEL_MONTHS[key as TentPanelKey] ?? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
}

/** Size of one tent display face (the first calendar panel). */
export function tentFaceSize(p: Pick<Product, 'widthMm' | 'heightMm' | 'tent'>) {
  const first = tentPagePanels(p)[0];
  return first ? { w: first.w, h: first.h } : { w: p.widthMm, h: p.heightMm };
}

export type { TentPanel } from './tent.js';
/** Flat layout of a folded tent: panels, fold lines (guides only) and the edges that carry bleed. */
export function tentLayout(doc: ProjectDoc): TentFlat {
  return tentFlat(doc.product);
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
export function deepMerge<T>(base: T, patch: unknown): T {
  if (!isObj(patch)) return (patch === undefined ? base : patch) as T;
  if (!isObj(base)) return patch as T;
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(patch)) out[k] = isObj(v) && isObj(out[k]) ? deepMerge(out[k], v) : v;
  return out as T;
}

/** Global design for a page with its explicit overrides merged in. */
export function resolveDesign(doc: ProjectDoc, key: PageKey): PageDesign {
  const info = pageInfo(doc, key);
  const global = doc.designs[info.design];
  const ov = doc.overrides[key];
  if (!ov) return global;
  return {
    background: ov.background !== undefined ? ov.background : global.background,
    elements: global.elements.map((e) => (ov.elements?.[e.id] ? (deepMerge(e, ov.elements[e.id]) as Element) : e)),
  };
}

export function hasOverride(doc: ProjectDoc, key: PageKey, elementId?: string): boolean {
  const ov = doc.overrides[key];
  if (!ov) return false;
  if (elementId) return !!ov.elements?.[elementId] && Object.keys(ov.elements[elementId]).length > 0;
  return ov.background !== undefined || Object.values(ov.elements ?? {}).some((p) => Object.keys(p).length > 0);
}

/** Set a value at a path inside an element, either globally or as a page override. */
export function setElementProp(doc: ProjectDoc, scope: 'all' | PageKey, design: DesignKey, elementId: string, path: string[], value: unknown): ProjectDoc {
  const build = (p: string[], v: unknown): Record<string, unknown> => (p.length === 1 ? { [p[0]]: v } : { [p[0]]: build(p.slice(1), v) });
  if (scope === 'all' || design !== 'month') {
    // Cover and backing have one page each, so their design is edited directly.
    const d = doc.designs[design];
    const elements = d.elements.map((e) => (e.id === elementId ? (deepMerge(e, build(path, value)) as Element) : e));
    let next: ProjectDoc = { ...doc, designs: { ...doc.designs, [design]: { ...d, elements } } };
    if (scope === 'all' && design === 'month') {
      // An "All months" change replaces the same property in any monthly override, so every month matches.
      next = { ...next, overrides: Object.fromEntries(Object.entries(next.overrides).map(([k, ov]) => [k, removePath(ov, elementId, path)])) };
    }
    return next;
  }
  const ov = doc.overrides[scope] ?? {};
  const elPatch = (ov.elements?.[elementId] ?? {}) as Record<string, unknown>;
  return {
    ...doc,
    overrides: { ...doc.overrides, [scope]: { ...ov, elements: { ...(ov.elements ?? {}), [elementId]: deepMerge(elPatch, build(path, value)) } } },
  };
}

function removePath(ov: ProjectDoc['overrides'][string], elementId: string, path: string[]) {
  const p = ov.elements?.[elementId];
  if (!p) return ov;
  const clone = structuredClone(p) as Record<string, unknown>;
  let cur: Record<string, unknown> | undefined = clone;
  for (let i = 0; i < path.length - 1 && cur; i++) cur = isObj(cur[path[i]]) ? (cur[path[i]] as Record<string, unknown>) : undefined;
  if (cur) delete cur[path[path.length - 1]];
  const prune = (o: Record<string, unknown>) => {
    for (const k of Object.keys(o)) if (isObj(o[k])) { prune(o[k] as Record<string, unknown>); if (!Object.keys(o[k] as object).length) delete o[k]; }
  };
  prune(clone);
  const elements = { ...(ov.elements ?? {}) };
  if (Object.keys(clone).length) elements[elementId] = clone; else delete elements[elementId];
  return { ...ov, elements };
}

export function resetOverride(doc: ProjectDoc, key: PageKey, elementId?: string): ProjectDoc {
  const overrides = { ...doc.overrides };
  if (!elementId) delete overrides[key];
  else if (overrides[key]?.elements) {
    const els = { ...overrides[key].elements };
    delete els[elementId];
    overrides[key] = { ...overrides[key], elements: els };
  }
  return { ...doc, overrides };
}

export interface ClearZone extends Rect { label: string }
export interface PageGeometry {
  trimW: number;
  trimH: number;
  bleed: number;
  safe: Rect;
  /** keep-clear zones: binding / hanging / rim clearance, or where a tear-off pad is glued */
  bindings: ClearZone[];
}
export function pageGeometry(doc: ProjectDoc, key?: PageKey): PageGeometry {
  const { widthMm, heightMm, bleedMm, safeMarginMm: m, bindingEdge, bindingClearanceMm: b } = doc.product;
  let W = widthMm, H = heightMm;
  const zones: ClearZone[] = [];
  const panel = doc.product.productType === 'tent' && key ? tentPanelSize(doc.product, key) : null;
  if (panel) ({ w: W, h: H } = panel);
  else if (key === 'carrier') {
    const c = doc.product.carrier;
    W = c.widthMm;
    H = c.heightMm;
    if (c.pad.w > 0 && c.pad.h > 0) zones.push({ x: c.pad.x, y: c.pad.y, w: c.pad.w, h: c.pad.h, label: 'Tear-off pad glued here' });
  } else {
    if (b > 0 && (bindingEdge === 'top' || bindingEdge === 'top-bottom')) zones.push({ x: 0, y: 0, w: W, h: b, label: 'Binding / hanging clearance' });
    if (b > 0 && bindingEdge === 'top-bottom') zones.push({ x: 0, y: H - b, w: W, h: b, label: 'Rim clearance' });
    if (b > 0 && bindingEdge === 'left') zones.push({ x: 0, y: 0, w: b, h: H, label: 'Binding clearance' });
  }
  return { trimW: W, trimH: H, bleed: bleedMm, safe: { x: m, y: m, w: W - 2 * m, h: H - 2 * m }, bindings: zones };
}

/** Text tokens available in text elements. */
export function expandTokens(text: string, doc: ProjectDoc, key: PageKey): string {
  const info = pageInfo(doc, key);
  const loc = getLocale(doc.calendar.locale);
  const month = info.month ? loc.months[info.month - 1] : '';
  const out = text
    .replace(/\{MONTH\}/g, month)
    .replace(/\{MONTH_SHORT\}/g, info.month ? loc.monthsShort[info.month - 1] : '')
    .replace(/\{YEAR\}/g, String(doc.year))
    .replace(/\{CAPTION\}/g, doc.captions[key] ?? '')
    .replace(/\{CONTACT\}/g, doc.brand.contact)
    .replace(/\{CUSTOMER\}/g, doc.brand.customerName)
    .replace(/\{COMPANY\}/g, doc.brand.company)
    .replace(/\{TAGLINE\}/g, doc.brand.tagline)
    .replace(/\{PHONE\}/g, doc.brand.phone)
    .replace(/\{EMAIL\}/g, doc.brand.email)
    .replace(/\{WEBSITE\}/g, doc.brand.website)
    .replace(/\{ADDRESS\}/g, doc.brand.address)
    .replace(/\{PERSON_NAME\}/g, doc.brand.personName)
    .replace(/\{PERSON_TITLE\}/g, doc.brand.personTitle);
  // Contact placeholders that are empty should not leave gaps or blank lines.
  if (!/\{(CONTACT|COMPANY|TAGLINE|PHONE|EMAIL|WEBSITE|ADDRESS|PERSON_NAME|PERSON_TITLE)\}/.test(text)) return out;
  return out.split('\n').map((l) => l.replace(/ {2,}/g, '   ').trim()).filter((l) => l !== '').join('\n');
}

/** Image slots of a design that hold a per-page photo (shared frames excluded), sorted. */
export function imageSlots(design: PageDesign): number[] {
  return [...new Set(design.elements.filter((e) => e.type === 'image' && !e.shared).map((e) => (e as { slot: number }).slot))].sort((a, b) => a - b);
}

/** Where a frame's photo is stored: the page itself, or "shared" for frames repeated on every page. */
export const photoPageKey = (pageKey: PageKey, el: { shared?: boolean }): PageKey => (el.shared ? 'shared' : pageKey);

/**
 * Auto-assign photos to pages in display order: cover (if any) first, then
 * January…December, each page's slots in order. Existing placements are replaced.
 */
export function autoAssignPhotos(doc: ProjectDoc, assetIds: string[]): ProjectDoc {
  const photos: ProjectDoc['photos'] = {};
  let i = 0;
  if (doc.photos.shared) photos.shared = doc.photos.shared;
  for (const p of calendarPages(doc)) {
    const slots = imageSlots(resolveDesign(doc, p.key));
    for (const s of slots) {
      if (i >= assetIds.length) break;
      photos[p.key] = { ...(photos[p.key] ?? {}), [s]: { assetId: assetIds[i++], fit: 'fill', zoom: 1, panX: 0, panY: 0 } };
    }
  }
  return { ...doc, photos };
}

/** Swap the photographs of two pages (drag-to-reorder in the thumbnail strip). */
export function swapPagePhotos(doc: ProjectDoc, a: PageKey, b: PageKey): ProjectDoc {
  const photos = { ...doc.photos };
  const pa = photos[a], pb = photos[b];
  if (pb) photos[a] = pb; else delete photos[a];
  if (pa) photos[b] = pa; else delete photos[b];
  return { ...doc, photos };
}

/**
 * Move a page's photographs to another position, shifting the pages in
 * between (like reordering a list): drag March onto January → March's photo
 * becomes January's, January's becomes February's, etc.
 */
export function movePagePhotos(doc: ProjectDoc, from: PageKey, to: PageKey): ProjectDoc {
  const keys = calendarPages(doc).map((p) => p.key);
  const fi = keys.indexOf(from), ti = keys.indexOf(to);
  if (fi < 0 || ti < 0 || fi === ti) return doc;
  const list = keys.map((k) => doc.photos[k]);
  const [moved] = list.splice(fi, 1);
  list.splice(ti, 0, moved);
  const photos: ProjectDoc['photos'] = { ...doc.photos };
  keys.forEach((k, i) => { if (list[i]) photos[k] = list[i]; else delete photos[k]; });
  return { ...doc, photos };
}

/**
 * Duplicate a project for another year: keeps images, styles, overrides,
 * captions and recurring events; drops year-specific datasets and asks for review.
 */
export function duplicateForYear(doc: ProjectDoc, year: number): { doc: ProjectDoc; reviewNotes: string[] } {
  const { sets, reviewNotes } = eventSetsForNewYear(doc.eventSets, doc.year, year);
  const cellOverrides = Object.fromEntries(Object.entries(doc.cellOverrides).filter(([k]) => +k.slice(0, 4) === year));
  if (Object.keys(cellOverrides).length !== Object.keys(doc.cellOverrides).length)
    reviewNotes.push('Individual day colours set for specific dates were removed because those dates belong to the old year.');
  reviewNotes.push(`Select the ${year} public holiday and school holiday datasets in Content → Holidays & events.`);
  return {
    doc: { ...structuredClone(doc), year, eventSets: sets, cellOverrides, checks: { ...doc.checks, acknowledged: [] } },
    reviewNotes,
  };
}

export interface ImpositionPlan {
  imp: Imposition | null;
  /** how pages go onto sheets */
  mode: 'repeat' | 'ganged' | 'duplex';
  sheets: number;
  /** tear-off pad carrier card, imposed on its own sheets */
  carrier: { imp: Imposition | null; sheets: number } | null;
}
/** What the imposed SRA3 export will contain (mirrors the server export). */
export function impositionPlan(doc: ProjectDoc): ImpositionPlan {
  const p = doc.product;
  const imp = computeImposition(p.widthMm, p.heightMm, p.bleedMm);
  const back = backingPage(doc);
  if (p.productType === 'tent') {
    // the flat piece: bleed only on the edges that carry it (DL tents: none on the tape strip's edge)
    const piece = tentPieceSize(p);
    const timp = p.bleedMm > 0 ? computeImpositionForPiece(piece.w, piece.h) : null;
    return { imp: timp, mode: 'repeat', sheets: timp ? 1 : 0, carrier: null };
  }
  if (p.productType === 'year') return { imp, mode: 'repeat', sheets: imp ? 1 + (back ? 1 : 0) : 0, carrier: null };
  const n = calendarPages(doc).length;
  let carrier: ImpositionPlan['carrier'] = null;
  if (back?.key === 'carrier') {
    const g = pageGeometry(doc, 'carrier');
    const cimp = computeImposition(g.trimW, g.trimH, g.bleed);
    carrier = { imp: cimp, sheets: cimp ? 1 : 0 };
  }
  if (!imp) return { imp, mode: p.duplex ? 'duplex' : 'ganged', sheets: 0, carrier };
  if (p.duplex) return { imp, mode: 'duplex', sheets: 2 * Math.ceil(Math.ceil(n / 2) / imp.perSheet), carrier };
  return { imp, mode: 'ganged', sheets: Math.ceil(n / imp.perSheet), carrier };
}
